import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import { Cancel, Close } from '@mui/icons-material';
import { useIntl } from 'react-intl';
import { useIdleTimer } from 'react-idle-timer';
import { useInterval } from 'usehooks-ts';
import { useAuth } from 'hook/useAuth';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

function IdleContainer({ timeout = 1800, promptTimeout = 5, children }) {
  const { formatMessage: msg } = useIntl();
  const { currentUser, logout } = useAuth();

  const [idleModalOpen, setIdleModalOpen] = useState(false);
  const [idleModalRemaining, setIdleModalRemaining] = useState(promptTimeout);

  const onPrompt = () => {
    setIdleModalOpen(true);
  };

  const onIdle = () => {
    setIdleModalOpen(false);
    logout();
  };

  const closeIdleModal = () => {
    reset();
    setIdleModalOpen(false);
  };

  const { reset, pause, getRemainingTime } = useIdleTimer({
    onPrompt,
    onIdle,
    timeout: (timeout ?? 120) * 1000,
    promptBeforeIdle: (promptTimeout ?? 120) * 1000
  });

  useEffect(() => {
    if (!currentUser?.user) {
      pause();
    }
  }, [currentUser?.user, pause]);

  useInterval(
    () => {
      setIdleModalRemaining(Math.ceil(getRemainingTime() / 1000));
    },
    idleModalOpen ? 1000 : null
  );

  return (
    <>
      {children}
      <Dialog
        open={currentUser?.user !== null && idleModalOpen}
        maxWidth="sm"
        fullWidth
        onClose={() => closeIdleModal()}
      >
        <DialogTitle>{msg({ id: 'authentication.confirmation.session.idle' })}</DialogTitle>
        <MDBox position="absolute" top={0} right={0}>
          <IconButton onClick={() => closeIdleModal()}>
            <Close />
          </IconButton>
        </MDBox>
        <DialogContent>
          <MDTypography>
            {msg(
              { id: 'authentication.confirmation.session.idle.details' },
              { remainingTime: idleModalRemaining }
            )}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton
            color="info"
            variant="gradient"
            startIcon={<Cancel />}
            autoFocus
            onClick={() => closeIdleModal()}
          >
            {msg({ id: 'action.continue' })}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

IdleContainer.propTypes = {
  timeout: PropTypes.number,
  promptTimeout: PropTypes.number,
  children: PropTypes.node
};

export default IdleContainer;
